import React, { useEffect, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Check, Plus, Pencil, Ban, CheckCircle2 } from 'lucide-react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';

const MESI = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
const fmt = (n) => `€ ${(n || 0).toFixed(2)}`;
const oggiISO = () => new Date().toISOString().slice(0, 10);

const VUOTO = { nome: '', importo: '', periodicita: 'MENSILE', giorno_scadenza: 1, mese_scadenza: 1, categoria_id: '', data_inizio: oggiISO(), data_fine: '', note: '' };

export default function SpeseRicorrenti() {
  const { user } = useAuth();
  const puoGestire = puoFare(user, 'banca', 'create');
  const puoDecidere = puoFare(user, 'banca', 'edit');

  const [tab, setTab] = useState('scadenze'); // scadenze | gestione
  const oggi = new Date();
  const [anno, setAnno] = useState(oggi.getFullYear());
  const [mese, setMese] = useState(oggi.getMonth() + 1);
  const [scadenze, setScadenze] = useState([]);
  const [loading, setLoading] = useState(true);

  const [spese, setSpese] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(VUOTO);

  const [decidendo, setDecidendo] = useState(null); // { scadenza, azione: 'conferma' }
  const [decisioneForm, setDecisioneForm] = useState({ importo: '', data_pagamento: oggiISO(), note: '' });

  const loadScadenze = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/spese-ricorrenti/scadenze?anno=${anno}&mese=${mese}`); setScadenze(r.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [anno, mese]);

  const loadGestione = useCallback(async () => {
    // il centro non si sceglie qui: lo dà la categoria
    const [sp, ca] = await Promise.all([
      api.get('/spese-ricorrenti'),
      api.get('/banca/categorie'),
    ]);
    setSpese(sp.data); setCategorie(ca.data);
  }, []);

  useEffect(() => { loadScadenze(); }, [loadScadenze]);
  useEffect(() => { loadGestione(); }, [loadGestione]);

  const navMese = (delta) => {
    let m = mese + delta, a = anno;
    if (m < 1) { m = 12; a--; } else if (m > 12) { m = 1; a++; }
    setMese(m); setAnno(a);
  };

  const apriNuova = () => { setEditing(null); setForm(VUOTO); setShowForm(true); };
  const apriModifica = (s) => {
    setEditing(s);
    setForm({
      nome: s.nome, importo: String(s.importo), periodicita: s.periodicita,
      giorno_scadenza: s.giorno_scadenza, mese_scadenza: s.mese_scadenza || 1,
      categoria_id: s.categoria_id || '',
      data_inizio: s.data_inizio.slice(0, 10), data_fine: s.data_fine ? s.data_fine.slice(0, 10) : '',
      note: s.note || '',
    });
    setShowForm(true);
  };

  const salvaSpesa = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/spese-ricorrenti/${editing.id}`, form);
      else await api.post('/spese-ricorrenti', form);
      setShowForm(false); setEditing(null); await loadGestione(); await loadScadenze();
    } catch (err) { alert(err.response?.data?.error || 'Errore'); }
  };

  const toggleAttiva = async (s) => {
    try { await api.patch(`/spese-ricorrenti/${s.id}/attiva`, { attiva: !s.attiva }); loadGestione(); }
    catch (err) { alert(err.response?.data?.error || 'Errore'); }
  };

  const apriConferma = (scadenza) => {
    setDecidendo(scadenza);
    setDecisioneForm({ importo: String(scadenza.importo_previsto), data_pagamento: oggiISO(), note: '' });
  };

  const confermaScadenza = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/spese-ricorrenti/scadenze/${decidendo.id}/conferma`, decisioneForm);
      setDecidendo(null); loadScadenze();
    } catch (err) { alert(err.response?.data?.error || 'Errore'); }
  };

  const salta = async (scadenza) => {
    if (!window.confirm(`Segnare "${scadenza.spesaRicorrente.nome}" come non dovuta questo mese?`)) return;
    try { await api.patch(`/spese-ricorrenti/scadenze/${scadenza.id}/salta`, {}); loadScadenze(); }
    catch (err) { alert(err.response?.data?.error || 'Errore'); }
  };

  const totalePrevisto = scadenze.reduce((s, r) => s + (r.stato === 'SALTATA' ? 0 : (r.importo_confermato ?? r.importo_previsto)), 0);
  const daConfermare = scadenze.filter(s => s.stato === 'DA_CONFERMARE').length;

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>PIANO SPESE</h1>
          <p style={s.sub}>Costi periodici che non sono compensi dipendenti (già tracciati da presenze/paghe): affitti, bollette, abbonamenti. Confermare una scadenza registra il movimento vero in Banca.</p>
        </div>
      </div>

      <div style={s.tabBar}>
        <button style={{ ...s.tab, ...(tab === 'scadenze' ? s.tabActive : {}) }} onClick={() => setTab('scadenze')}>SCADENZE DEL MESE</button>
        <button style={{ ...s.tab, ...(tab === 'gestione' ? s.tabActive : {}) }} onClick={() => setTab('gestione')}>GESTISCI SPESE RICORRENTI</button>
      </div>

      {tab === 'scadenze' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <button style={s.navBtn} onClick={() => navMese(-1)}><ChevronLeft size={16} /></button>
            <span style={{ fontFamily: "'Barlow Condensed'", fontSize: 'var(--font-size-xl)', fontWeight: '800', minWidth: '180px', textAlign: 'center' }}>{MESI[mese - 1]} {anno}</span>
            <button style={s.navBtn} onClick={() => navMese(1)}><ChevronRight size={16} /></button>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '14px', alignItems: 'center' }}>
              {daConfermare > 0 && <span style={{ fontSize: 'var(--font-size-xs)', color: '#F59E0B', fontWeight: '700' }}>{daConfermare} da confermare</span>}
              <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: '800', color: '#F4600C' }}>{fmt(totalePrevisto)} previsto</span>
            </div>
          </div>

          {loading && <div style={{ color: '#666', padding: '20px' }}>Caricamento...</div>}
          {!loading && scadenze.length === 0 && (
            <div style={{ color: 'var(--text-dim)', padding: '30px', textAlign: 'center' }}>Nessuna spesa ricorrente prevista per questo mese.</div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {scadenze.map(sc => {
              const sr = sc.spesaRicorrente;
              const colore = sc.stato === 'CONFERMATA' ? '#22C55E' : sc.stato === 'SALTATA' ? '#555' : '#F59E0B';
              return (
                <div key={sc.id} style={{ ...s.riga, opacity: sc.stato === 'SALTATA' ? 0.5 : 1 }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: sr.categoria?.colore || '#888', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 'var(--font-size-sm)' }}>{sr.nome}</div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: '#666' }}>
                      {sr.categoria?.nome || 'Senza categoria'}{sr.categoria?.centro && ` · ${sr.categoria.centro.nome}`} · scade il {sr.giorno_scadenza}
                    </div>
                  </div>
                  <span style={{ fontSize: '10px', fontWeight: 800, letterSpacing: '1px', padding: '4px 10px', borderRadius: '20px', color: colore, background: `${colore}20` }}>
                    {sc.stato === 'DA_CONFERMARE' ? 'DA CONFERMARE' : sc.stato === 'CONFERMATA' ? 'CONFERMATA' : 'SALTATA'}
                  </span>
                  <div style={{ minWidth: '90px', textAlign: 'right', fontWeight: 800, fontFamily: "'Barlow Condensed'", fontSize: 'var(--font-size-lg)' }}>
                    {fmt(sc.importo_confermato ?? sc.importo_previsto)}
                  </div>
                  {puoDecidere && sc.stato === 'DA_CONFERMARE' && (
                    <>
                      <button style={{ ...s.iconBtn, color: '#22C55E' }} title="Conferma e registra" onClick={() => apriConferma(sc)}><CheckCircle2 size={14} /></button>
                      <button style={{ ...s.iconBtn, color: '#888' }} title="Salta questo mese" onClick={() => salta(sc)}><Ban size={14} /></button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'gestione' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '14px' }}>
            {puoGestire && <button style={s.btnPri} onClick={apriNuova}><Plus size={14} /> NUOVA SPESA RICORRENTE</button>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {spese.map(sp => (
              <div key={sp.id} style={{ ...s.riga, opacity: sp.attiva ? 1 : 0.5 }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: sp.categoria?.colore || '#888', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 'var(--font-size-sm)' }}>{sp.nome}</div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: '#666' }}>
                    {sp.periodicita === 'MENSILE' ? `Mensile, il giorno ${sp.giorno_scadenza}` : `Annuale, ${MESI[sp.mese_scadenza - 1]} (giorno ${sp.giorno_scadenza})`}
                    {sp.categoria && ` · ${sp.categoria.nome}`}{sp.categoria?.centro && ` · ${sp.categoria.centro.nome}`}
                    {!sp.attiva && ' · disattivata'}
                  </div>
                </div>
                <div style={{ minWidth: '90px', textAlign: 'right', fontWeight: 800, fontFamily: "'Barlow Condensed'", fontSize: 'var(--font-size-lg)' }}>{fmt(sp.importo)}</div>
                {puoGestire && <button style={s.iconBtn} onClick={() => apriModifica(sp)}><Pencil size={13} /></button>}
                {puoGestire && <button style={s.iconBtn} onClick={() => toggleAttiva(sp)}>{sp.attiva ? 'Disattiva' : 'Riattiva'}</button>}
              </div>
            ))}
            {spese.length === 0 && <div style={{ color: 'var(--text-dim)', padding: '20px' }}>Nessuna spesa ricorrente configurata.</div>}
          </div>
        </div>
      )}

      {/* MODAL NUOVA/MODIFICA SPESA RICORRENTE */}
      {showForm && (
        <div style={s.overlay}>
          <form onSubmit={salvaSpesa} style={s.modal}>
            <div style={s.modalTitle}>{editing ? 'MODIFICA' : 'NUOVA'} SPESA RICORRENTE</div>
            <div style={s.field}><label style={s.label}>NOME *</label><input style={s.input} value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} required autoFocus /></div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ ...s.field, flex: 1 }}><label style={s.label}>IMPORTO PREVENTIVATO €</label><input style={s.input} type="number" step="0.01" min="0.01" value={form.importo} onChange={e => setForm(f => ({ ...f, importo: e.target.value }))} required /></div>
              <div style={{ ...s.field, flex: 1 }}><label style={s.label}>PERIODICITÀ</label>
                <select style={s.input} value={form.periodicita} onChange={e => setForm(f => ({ ...f, periodicita: e.target.value }))}>
                  <option value="MENSILE">Mensile</option>
                  <option value="ANNUALE">Annuale</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ ...s.field, flex: 1 }}><label style={s.label}>GIORNO SCADENZA (1-31)</label><input style={s.input} type="number" min="1" max="31" value={form.giorno_scadenza} onChange={e => setForm(f => ({ ...f, giorno_scadenza: e.target.value }))} /></div>
              {form.periodicita === 'ANNUALE' && (
                <div style={{ ...s.field, flex: 1 }}><label style={s.label}>MESE SCADENZA</label>
                  <select style={s.input} value={form.mese_scadenza} onChange={e => setForm(f => ({ ...f, mese_scadenza: e.target.value }))}>
                    {MESI.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ ...s.field, flex: 1 }}><label style={s.label}>CATEGORIA</label>
                <select style={s.input} value={form.categoria_id} onChange={e => setForm(f => ({ ...f, categoria_id: e.target.value }))}>
                  <option value="">— nessuna —</option>
                  {categorie.filter(c => c.attiva).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ ...s.field, flex: 1 }}><label style={s.label}>VALIDA DAL</label><input style={s.input} type="date" value={form.data_inizio} onChange={e => setForm(f => ({ ...f, data_inizio: e.target.value }))} /></div>
              <div style={{ ...s.field, flex: 1 }}><label style={s.label}>VALIDA FINO AL (opzionale)</label><input style={s.input} type="date" value={form.data_fine} onChange={e => setForm(f => ({ ...f, data_fine: e.target.value }))} /></div>
            </div>
            <div style={s.field}><label style={s.label}>NOTE</label><input style={s.input} value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} /></div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '6px' }}>
              <button type="button" style={s.btnSec} onClick={() => setShowForm(false)}>ANNULLA</button>
              <button type="submit" style={s.btnPri}>SALVA</button>
            </div>
          </form>
        </div>
      )}

      {/* MODAL CONFERMA SCADENZA */}
      {decidendo && (
        <div style={s.overlay}>
          <form onSubmit={confermaScadenza} style={s.modal}>
            <div style={s.modalTitle}>CONFERMA PAGAMENTO</div>
            <p style={{ color: '#888', fontSize: 'var(--font-size-sm)', marginBottom: '6px' }}>{decidendo.spesaRicorrente.nome} — previsto {fmt(decidendo.importo_previsto)}</p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ ...s.field, flex: 1 }}><label style={s.label}>IMPORTO EFFETTIVO €</label><input style={s.input} type="number" step="0.01" min="0.01" value={decisioneForm.importo} onChange={e => setDecisioneForm(f => ({ ...f, importo: e.target.value }))} required autoFocus /></div>
              <div style={{ ...s.field, flex: 1 }}><label style={s.label}>DATA PAGAMENTO</label><input style={s.input} type="date" value={decisioneForm.data_pagamento} onChange={e => setDecisioneForm(f => ({ ...f, data_pagamento: e.target.value }))} /></div>
            </div>
            <div style={s.field}><label style={s.label}>NOTE (opzionale)</label><input style={s.input} value={decisioneForm.note} onChange={e => setDecisioneForm(f => ({ ...f, note: e.target.value }))} /></div>
            <p style={{ color: '#666', fontSize: '10px', marginTop: '-4px' }}>Confermando, il sistema registra subito un movimento in Movimenti Bancari con questo importo — non invia nessun bonifico reale, serve solo a tracciare che il pagamento è stato fatto da te.</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '6px' }}>
              <button type="button" style={s.btnSec} onClick={() => setDecidendo(null)}>ANNULLA</button>
              <button type="submit" style={{ ...s.btnPri, background: '#22C55E' }}><Check size={13} /> CONFERMA E REGISTRA</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'var(--font-size-title)', fontWeight: '800', letterSpacing: '2px' },
  sub: { color: 'var(--text-dim)', fontSize: 'var(--font-size-sm)', marginTop: '4px', maxWidth: '620px' },
  tabBar: { display: 'flex', gap: '4px', marginBottom: '18px', borderBottom: '1px solid var(--border)' },
  tab: { padding: '9px 16px', background: 'transparent', border: 'none', borderBottom: '2px solid transparent', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: 700, letterSpacing: '1px', color: '#666' },
  tabActive: { color: '#F4600C', borderBottomColor: '#F4600C' },
  navBtn: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '8px', color: 'var(--text)', cursor: 'pointer', display: 'flex' },
  riga: { display: 'flex', alignItems: 'center', gap: '12px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px 16px' },
  iconBtn: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '6px', padding: '7px 9px', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', fontSize: 'var(--font-size-xs)', fontWeight: 700 },
  btnPri: { display: 'flex', alignItems: 'center', gap: '8px', background: '#F4600C', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 14px', fontSize: 'var(--font-size-xs)', fontWeight: '700', cursor: 'pointer' },
  btnSec: { background: 'var(--border)', color: 'var(--text)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '9px 14px', fontSize: 'var(--font-size-xs)', fontWeight: '700', cursor: 'pointer' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'var(--bg-card)', border: '1px solid #222', borderRadius: '14px', padding: '24px', minWidth: '420px', maxWidth: '480px', width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' },
  modalTitle: { fontFamily: "'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight: '700', letterSpacing: '2px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: 'var(--font-size-xs)', letterSpacing: '2px', fontWeight: '700', color: 'var(--text-dim)' },
  input: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '9px 12px', color: 'var(--text)', fontSize: 'var(--font-size-sm)', outline: 'none', width: '100%', boxSizing: 'border-box' },
};
